/**
 * Kynetik Admin Panel Theme
 *
 * Extends BigDesign's default theme with Kynetik brand colors.
 * BigDesign has limited customization, but we can override colors.
 *
 * Primary: Kynetik Indigo (#4F46E5)
 */

import { createTheme } from '@bigcommerce/big-design-theme';

/**
 * Kynetik brand colors
 */
export const brandColors = {
  primary: {
    50: '#EEF2FF',
    100: '#E0E7FF',
    200: '#C7D2FE',
    300: '#A5B4FC',
    400: '#818CF8',
    500: '#6366F1',
    600: '#4F46E5', // Main brand
    700: '#4338CA',
    800: '#3730A3',
    900: '#312E81',
    950: '#1E1B4B',
  },
  accent: {
    500: '#F59E0B',
    600: '#D97706',
  },
  success: {
    500: '#10B981',
    600: '#059669',
  },
  error: {
    500: '#EF4444',
    600: '#DC2626',
  },
  warning: {
    500: '#F59E0B',
    600: '#D97706',
  },
} as const;

/**
 * Custom BigDesign theme with Kynetik branding
 *
 * Note: BigDesign's theme customization is limited to htmlFontSize.
 * We use the default theme and supplement with CSS variable overrides.
 */
export const kynetikTheme = createTheme();

/**
 * CSS custom properties for use in styled-components
 *
 * Use these for additional styling that BigDesign doesn't cover.
 */
export const cssVariables = `
  :root {
    /* Primary brand */
    --kynetik-primary-50: ${brandColors.primary[50]};
    --kynetik-primary-100: ${brandColors.primary[100]};
    --kynetik-primary-200: ${brandColors.primary[200]};
    --kynetik-primary-300: ${brandColors.primary[300]};
    --kynetik-primary-400: ${brandColors.primary[400]};
    --kynetik-primary-500: ${brandColors.primary[500]};
    --kynetik-primary-600: ${brandColors.primary[600]};
    --kynetik-primary-700: ${brandColors.primary[700]};
    --kynetik-primary-800: ${brandColors.primary[800]};
    --kynetik-primary-900: ${brandColors.primary[900]};
    --kynetik-primary-950: ${brandColors.primary[950]};

    /* Accent */
    --kynetik-accent-500: ${brandColors.accent[500]};
    --kynetik-accent-600: ${brandColors.accent[600]};

    /* Semantic */
    --kynetik-success: ${brandColors.success[500]};
    --kynetik-error: ${brandColors.error[500]};
    --kynetik-warning: ${brandColors.warning[500]};

    /* Focus ring */
    --focus-ring: 0 0 0 4px ${brandColors.primary[200]};
  }
`;

/**
 * Styled-components global style overrides
 *
 * These supplement BigDesign styles with Kynetik branding.
 * Since BigDesign doesn't support color customization via createTheme,
 * we override the default blue (#3C64F4) with Kynetik Indigo (#4F46E5).
 */
export const globalStyleOverrides = `
  ${cssVariables}

  /* Override BigDesign primary buttons */
  button[class*="StyledButton"][class*="primary"] {
    background-color: var(--kynetik-primary-600) !important;

    &:hover:not(:disabled) {
      background-color: var(--kynetik-primary-700) !important;
    }

    &:focus {
      box-shadow: var(--focus-ring);
    }
  }

  /* Override link colors */
  a {
    color: var(--kynetik-primary-600);

    &:hover {
      color: var(--kynetik-primary-700);
    }
  }

  /* Badge primary variant */
  span[class*="Badge"][class*="secondary"] {
    background-color: var(--kynetik-primary-100);
    color: var(--kynetik-primary-700);
  }

  /* Tabs active indicator */
  button[class*="TabItem"][aria-selected="true"] {
    border-bottom-color: var(--kynetik-primary-600) !important;
    color: var(--kynetik-primary-600) !important;
  }

  /* Checkbox and Radio checked state */
  input[type="checkbox"]:checked + span,
  input[type="radio"]:checked + span {
    background-color: var(--kynetik-primary-600) !important;
    border-color: var(--kynetik-primary-600) !important;
  }

  /* Switch checked state */
  [class*="Switch"][class*="checked"] [class*="Slider"] {
    background-color: var(--kynetik-primary-600) !important;
  }

  /* Progress bar */
  [class*="ProgressBar"] [class*="Filler"] {
    background-color: var(--kynetik-primary-600) !important;
  }

  /* Panel styling */
  div[class*="Panel"] {
    border-radius: 8px;
  }

  /* Focus rings on inputs */
  input:focus, select:focus, textarea:focus {
    border-color: var(--kynetik-primary-600) !important;
    box-shadow: 0 0 0 1px var(--kynetik-primary-600) !important;
  }
`;

export default kynetikTheme;
